import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Download } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getCheckoutStatus, downloadUrl } from "@/lib/api";

const MAX_ATTEMPTS = 8;

export const PaymentReturn = () => {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("checkout") === "cancelled";
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("checking"); // checking | paid | error

  const clearQuery = () => {
    setParams({}, { replace: true });
  };

  const poll = useCallback(
    async (attempt) => {
      if (attempt >= MAX_ATTEMPTS) {
        setState("error");
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (cancelled) {
      toast.info("Checkout cancelled. You can pick up where you left off.");
      clearQuery();
    }
  }, [cancelled]);

  useEffect(() => {
    if (sessionId) {
      setOpen(true);
      setState("checking");
      poll(0);
    }
  }, [sessionId]);

  const close = () => {
    setOpen(false);
    clearQuery();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && state !== "checking" && close()}>
      <DialogContent className="bg-[#121118] border-white/10 text-white text-center" data-testid="payment-return-dialog">
        {state === "checking" && (
          <div className="py-8 flex flex-col items-center gap-5" data-testid="payment-checking">
            <Loader2 size={40} className="animate-spin text-[var(--neon-cyan)]" />
            <h3 className="font-future text-xl">Confirming your payment…</h3>
            <p className="text-white/50 text-sm">This only takes a moment. Please don&apos;t close this window.</p>
          </div>
        )}

        {state === "paid" && (
          <div className="py-8 flex flex-col items-center gap-5" data-testid="payment-success">
            <CheckCircle2 size={44} className="text-[var(--neon-cyan)]" />
            <h3 className="font-future text-2xl">Payment confirmed</h3>
            <p className="text-white/60 text-sm max-w-sm">
              Thank you. Your guide is ready — the download link is also valid if you need it again.
            </p>
            <a
              data-testid="download-btn"
              href={downloadUrl(sessionId)}
              className="mt-2 label-caps px-7 py-4 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 flex items-center gap-2"
            >
              <Download size={16} /> Download your guide
            </a>
            <button onClick={close} className="label-caps text-white/40 hover:text-white transition-colors duration-300 mt-1">
              Close
            </button>
          </div>
        )}

        {state === "error" && (
          <div className="py-8 flex flex-col items-center gap-5" data-testid="payment-error">
            <XCircle size={44} className="text-red-400" />
            <h3 className="font-future text-xl">We couldn&apos;t confirm your payment</h3>
            <p className="text-white/60 text-sm max-w-sm">
              If you were charged, please contact us and we&apos;ll sort it out right away.
            </p>
            <button onClick={close} className="label-caps px-7 py-3 rounded-full border border-white/25 hover:border-white transition-colors duration-300">
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
